var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineComponent as _defineComponent } from 'vue';
import { faCheckCircle, faUndo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { BAlert, BLink, BModal } from "bootstrap-vue";
import { computed, ref, watch } from "vue";
import { createDatasetCollection } from "@/components/History/model/queries";
import { useCollectionBuilderItemsStore } from "@/stores/collectionBuilderItemsStore";
import { useHistoryStore } from "@/stores/historyStore";
import localize from "@/utils/localization";
import { orList } from "@/utils/strings";
import ListCollectionCreator from "./ListCollectionCreator.vue";
import PairCollectionCreator from "./PairCollectionCreator.vue";
import PairedListCollectionCreator from "./PairedListCollectionCreator.vue";
import Heading from "@/components/Common/Heading.vue";
import GenericItem from "@/components/History/Content/GenericItem.vue";
import LoadingSpan from "@/components/LoadingSpan.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'CollectionCreatorModal',
    props: {
        historyId: null,
        showModal: { type: Boolean },
        collectionType: null,
        selectedItems: null,
        defaultHideSourceItems: { type: Boolean },
        extensions: null,
        fromRulesInput: { type: Boolean },
        hideModalOnCreate: { type: Boolean },
        filterText: null
    },
    emits: ["created-collection", "update:show-modal"],
    setup(__props, { emit }) {
        const props = __props;
        /** Computed toggle that handles opening and closing the modal */
        const localShowToggle = computed({
            get: () => props.showModal,
            set: (value) => {
                emit("update:show-modal", value);
            },
        });
        // Create Collection refs
        const creatingCollection = ref(false);
        const createCollectionError = ref(null);
        const createdCollection = ref(null);
        // History items variables
        const historyItemsError = ref(null);
        const collectionItemsStore = useCollectionBuilderItemsStore();
        const historyStore = useHistoryStore();
        const history = computed(() => historyStore.getHistoryById(props.historyId));
        const historyId = computed(() => props.historyId);
        const localFilterText = computed(() => props.filterText || "");
        const historyUpdateTime = computed(() => { var _a; return (_a = history.value) === null || _a === void 0 ? void 0 : _a.update_time; });
        const isFetchingItems = computed(() => collectionItemsStore.isFetching[localFilterText.value]);
        const historyDatasets = computed(() => {
            if (collectionItemsStore.cachedDatasetsForFilterText) {
                return collectionItemsStore.cachedDatasetsForFilterText[localFilterText.value] || [];
            }
            else {
                return [];
            }
        });
        /** Flag for the initial fetch of history items */
        const initialFetch = ref(false);
        /** Whether a list of items was selected to create a collection from */
        const fromSelection = computed(() => { var _a; return !!((_a = props.selectedItems) === null || _a === void 0 ? void 0 : _a.length); });
        /** Items to create the collection from */
        const creatorItems = computed(() => (fromSelection.value ? props.selectedItems : historyDatasets.value));
        watch(() => localShowToggle.value, (show) => __awaiter(this, void 0, void 0, function* () {
            if (show) {
                yield fetchHistoryDatasets();
                if (!initialFetch.value) {
                    initialFetch.value = true;
                }
            }
        }), { immediate: true });
        // Fetch items when history ID or update time changes, only if localShowToggle is true
        watch([historyId, historyUpdateTime, localFilterText], () => __awaiter(this, void 0, void 0, function* () {
            if (localShowToggle.value) {
                yield fetchHistoryDatasets();
            }
        }));
        // If there is a change in `historyDatasets`, but we have selected items, we should update the selected items
        watch(() => historyDatasets.value, (newDatasets) => {
            var _a;
            if (fromSelection.value) {
                // find each selected item in the new datasets, and update it
                (_a = props.selectedItems) === null || _a === void 0 ? void 0 : _a.forEach((selectedItem) => {
                    const newDataset = newDatasets.find((dataset) => dataset.id === selectedItem.id);
                    if (newDataset) {
                        Object.assign(selectedItem, newDataset);
                    }
                });
            }
        });
        const extensionInTitle = computed(() => {
            const extensions = props.extensions;
            if (!extensions || extensions.length == 0 || extensions.indexOf("data") >= 0) {
                return "";
            }
            else {
                return orList(extensions);
            }
        });
        const modalTitle = computed(() => {
            if (props.collectionType === "list") {
                return localize(`Create a list of ${fromSelection.value ? "selected" : ""} ${extensionInTitle.value} datasets`);
            }
            else if (props.collectionType === "list:paired") {
                return localize(`Create a list of ${fromSelection.value ? "selected" : ""} ${extensionInTitle.value} paired datasets`);
            }
            else if (props.collectionType === "paired") {
                return localize(`Create a ${extensionInTitle.value} paired dataset collection ${fromSelection.value ? "from selected items" : ""}`);
            }
            else {
                return localize("Create a collection");
            }
        });
        // Methods
        function createListCollection(elements, name, hideSourceItems) {
            const returnedElems = elements.map((element) => ({
                id: element.id,
                name: element.name,
                //TODO: this allows for list:list even if the implementation does not - reconcile
                src: "src" in element ? element.src : element.history_content_type == "dataset" ? "hda" : "hdca",
            }));
            return createHDCA(returnedElems, "list", name, hideSourceItems);
        }
        function createListPairedCollection(elements, name, hideSourceItems) {
            const returnedElems = elements.map((pair) => ({
                collection_type: "paired",
                src: "new_collection",
                name: pair.name,
                element_identifiers: [
                    {
                        name: "forward",
                        id: pair.forward.id,
                        src: "src" in pair.forward ? pair.forward.src : "hda",
                    },
                    {
                        name: "reverse",
                        id: pair.reverse.id,
                        src: "src" in pair.reverse ? pair.reverse.src : "hda",
                    },
                ],
            }));
            return createHDCA(returnedElems, "list:paired", name, hideSourceItems);
        }
        function createPairedCollection(elements, name, hideSourceItems) {
            const { forward, reverse } = elements;
            const returnedElems = [
                { name: "forward", src: "src" in forward ? forward.src : "hda", id: forward.id },
                { name: "reverse", src: "src" in reverse ? reverse.src : "hda", id: reverse.id },
            ];
            return createHDCA(returnedElems, "paired", name, hideSourceItems);
        }
        function createHDCA(element_identifiers, collection_type, name, hide_source_items, options = {}) {
            return __awaiter(this, void 0, void 0, function* () {
                try {
                    creatingCollection.value = true;
                    const collection = yield createDatasetCollection(history.value, {
                        collection_type,
                        name,
                        hide_source_items,
                        element_identifiers,
                        options,
                    });
                    emit("created-collection", collection);
                    createdCollection.value = collection;
                    if (props.hideModalOnCreate) {
                        hideModal();
                    }
                }
                catch (error) {
                    createCollectionError.value = error;
                }
                finally {
                    creatingCollection.value = false;
                }
            });
        }
        function fetchHistoryDatasets() {
            return __awaiter(this, void 0, void 0, function* () {
                const { error } = yield collectionItemsStore.fetchDatasetsForFiltertext(historyId.value, historyUpdateTime.value, localFilterText.value);
                if (error) {
                    historyItemsError.value = error;
                    console.error("Error fetching history items:", historyItemsError.value);
                }
                else {
                    historyItemsError.value = null;
                }
            });
        }
        function hideModal() {
            localShowToggle.value = false;
        }
        function resetModal() {
            createCollectionError.value = null;
            createdCollection.value = null;
        }
        return { __sfc: true, props, emit, localShowToggle, creatingCollection, createCollectionError, createdCollection, historyItemsError, collectionItemsStore, historyStore, history, historyId, localFilterText, historyUpdateTime, isFetchingItems, historyDatasets, initialFetch, fromSelection, creatorItems, extensionInTitle, modalTitle, createListCollection, createListPairedCollection, createPairedCollection, createHDCA, fetchHistoryDatasets, hideModal, resetModal, faCheckCircle, faUndo, FontAwesomeIcon, BAlert, BLink, BModal, localize, ListCollectionCreator, PairCollectionCreator, PairedListCollectionCreator, Heading, GenericItem, LoadingSpan };
    }
});
